import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Bell,
  BookOpen,
  Globe,
  Lock,
  Users2,
  Building2,
  Tag,
  Sparkles,
  Save,
  Plus,
  X,
  Settings,
  Shield,
  Eye,
  Mail,
  Zap,
  Network,
} from 'lucide-react';
import { researchService } from '@/services/api/research';
import type { ResearchSettings, AccessLevel, Citation } from '@/types/research';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const ACCESS_LEVEL_CONFIG: Record<AccessLevel, { icon: React.ReactNode; label: string; description: string }> = {
  private: { icon: <Lock className="h-4 w-4" />, label: 'Private', description: 'Only you can access' },
  team: { icon: <Users2 className="h-4 w-4" />, label: 'Team', description: 'Your team members' },
  department: { icon: <Building2 className="h-4 w-4" />, label: 'Department', description: 'Your department' },
  institution: { icon: <Building2 className="h-4 w-4" />, label: 'Institution', description: 'Your institution' },
  public: { icon: <Globe className="h-4 w-4" />, label: 'Public', description: 'Anyone can view' },
};

const CITATION_FORMATS: { value: Citation['format']; label: string }[] = [
  { value: 'APA', label: 'APA 7th Edition' },
  { value: 'MLA', label: 'MLA 9th Edition' },
  { value: 'Chicago', label: 'Chicago 17th' },
  { value: 'Harvard', label: 'Harvard' },
  { value: 'IEEE', label: 'IEEE' },
  { value: 'Vancouver', label: 'Vancouver' },
];

const SUGGESTED_INTERESTS = [
  'Climate Change', 'Machine Learning', 'Quantum Computing', 'Marine Biology',
  'Gene Editing', 'Renewable Energy', 'Artificial Intelligence', 'Data Science',
  'Environmental Science', 'Biotechnology', 'Materials Science', 'Neuroscience',
];

export function ResearchSettingsPage() {
  const { data: settings, isLoading } = useQuery({
    queryKey: ['research-settings'],
    queryFn: () => researchService.getSettings(),
  });

  const [localSettings, setLocalSettings] = useState<ResearchSettings | null>(null);
  const [newInterest, setNewInterest] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Initialize local settings when data loads
  const currentSettings = localSettings || settings;

  const handleNotificationChange = (key: keyof ResearchSettings['notifications'], value: boolean) => {
    if (!currentSettings) return;
    setLocalSettings({
      ...currentSettings,
      notifications: {
        ...currentSettings.notifications,
        [key]: value,
      },
    });
  };

  const handleAddInterest = (interest: string) => {
    if (!currentSettings || !interest.trim()) return;
    if (currentSettings.researchInterests.includes(interest)) return;

    setLocalSettings({
      ...currentSettings,
      researchInterests: [...currentSettings.researchInterests, interest.trim()],
    });
    setNewInterest('');
  };

  const handleRemoveInterest = (interest: string) => {
    if (!currentSettings) return;
    setLocalSettings({
      ...currentSettings,
      researchInterests: currentSettings.researchInterests.filter((i) => i !== interest),
    });
  };

  const handleSave = async () => {
    if (!localSettings) return;

    setIsSaving(true);
    try {
      await researchService.updateSettings(localSettings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Research Settings</h1>
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Research Settings</h1>
          <p className="text-muted-foreground">
            Configure your research preferences, notifications, and access controls
          </p>
        </div>
        <Button onClick={handleSave} disabled={isSaving || !localSettings}>
          <Save className="mr-2 h-4 w-4" />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification Preferences
            </CardTitle>
            <CardDescription>
              Control when and how you receive research alerts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">New Research Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when new research matching your interests is published
                </p>
              </div>
              <Switch
                checked={currentSettings?.notifications.newResearchAlerts}
                onCheckedChange={(checked) => handleNotificationChange('newResearchAlerts', checked)}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Citation Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when your research is cited by others
                </p>
              </div>
              <Switch
                checked={currentSettings?.notifications.citationAlerts}
                onCheckedChange={(checked) => handleNotificationChange('citationAlerts', checked)}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Collaboration Requests</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when researchers want to collaborate
                </p>
              </div>
              <Switch
                checked={currentSettings?.notifications.collaborationRequests}
                onCheckedChange={(checked) => handleNotificationChange('collaborationRequests', checked)}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Weekly Digest</Label>
                <p className="text-sm text-muted-foreground">
                  Receive a weekly summary of research activity
                </p>
              </div>
              <Switch
                checked={currentSettings?.notifications.weeklyDigest}
                onCheckedChange={(checked) => handleNotificationChange('weeklyDigest', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Defaults */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Default Settings
            </CardTitle>
            <CardDescription>
              Configure defaults for new resources and citations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Default Access Level</Label>
              <Select
                value={currentSettings?.defaultAccessLevel}
                onValueChange={(value) =>
                  setLocalSettings({
                    ...currentSettings!,
                    defaultAccessLevel: value as AccessLevel,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ACCESS_LEVEL_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {config.icon}
                        <div>
                          <span className="font-medium">{config.label}</span>
                          <span className="text-muted-foreground"> - {config.description}</span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                This will be the default access level when uploading new resources
              </p>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Preferred Citation Format</Label>
              <Select
                value={currentSettings?.preferredCitationFormat}
                onValueChange={(value) =>
                  setLocalSettings({
                    ...currentSettings!,
                    preferredCitationFormat: value as Citation['format'],
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CITATION_FORMATS.map((format) => (
                    <SelectItem key={format.value} value={format.value}>
                      {format.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Your preferred format for generating citations
              </p>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  AI Auto-Tagging
                </Label>
                <p className="text-sm text-muted-foreground">
                  Automatically tag uploaded resources using AI
                </p>
              </div>
              <Switch
                checked={currentSettings?.autoTagging}
                onCheckedChange={(checked) =>
                  setLocalSettings({
                    ...currentSettings!,
                    autoTagging: checked,
                  })
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Research Interests */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Research Interests
            </CardTitle>
            <CardDescription>
              Add topics you're interested in to receive personalized recommendations and alerts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Interests */}
            <div className="flex flex-wrap gap-2">
              {currentSettings?.researchInterests.map((interest) => (
                <Badge key={interest} variant="secondary" className="gap-1 pr-1">
                  {interest}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 ml-1 hover:bg-destructive/20"
                    onClick={() => handleRemoveInterest(interest)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
              {(!currentSettings?.researchInterests || currentSettings.researchInterests.length === 0) && (
                <p className="text-sm text-muted-foreground">No interests added yet</p>
              )}
            </div>

            {/* Add New Interest */}
            <div className="flex gap-2">
              <Input
                placeholder="Add a research interest..."
                value={newInterest}
                onChange={(e) => setNewInterest(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddInterest(newInterest);
                  }
                }}
              />
              <Button onClick={() => handleAddInterest(newInterest)}>
                <Plus className="mr-2 h-4 w-4" />
                Add
              </Button>
            </div>

            {/* Suggested Interests */}
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">Suggested Topics</Label>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_INTERESTS
                  .filter((i) => !currentSettings?.researchInterests.includes(i))
                  .slice(0, 8)
                  .map((interest) => (
                    <Badge
                      key={interest}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary/10"
                      onClick={() => handleAddInterest(interest)}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      {interest}
                    </Badge>
                  ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Settings Cards */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Privacy */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-5 w-5" />
              Privacy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Show profile publicly</Label>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Allow collaboration requests</Label>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Show search history</Label>
              <Switch />
            </div>
          </CardContent>
        </Card>

        {/* Data & Exports */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="h-5 w-5" />
              Data & Exports
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button variant="outline" className="w-full justify-start">
              <BookOpen className="mr-2 h-4 w-4" />
              Export All Citations
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <Eye className="mr-2 h-4 w-4" />
              Download Activity Report
            </Button>
            <Button variant="outline" className="w-full justify-start">
              <Mail className="mr-2 h-4 w-4" />
              Export Contact List
            </Button>
          </CardContent>
        </Card>

        {/* Integrations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Network className="h-5 w-5" />
              Integrations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <img
                  src="https://info.orcid.org/wp-content/uploads/2019/11/orcid_16x16.png"
                  alt="ORCID"
                  className="h-5 w-5"
                />
                <span className="text-sm font-medium">ORCID</span>
              </div>
              <Badge variant="default" className="bg-green-500">Connected</Badge>
            </div>
            <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-blue-500" />
                <span className="text-sm font-medium">Google Scholar</span>
              </div>
              <Button variant="outline" size="sm">Connect</Button>
            </div>
            <div className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-orange-500" />
                <span className="text-sm font-medium">Mendeley</span>
              </div>
              <Button variant="outline" size="sm">Connect</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>
            Irreversible and destructive actions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Clear Search History</p>
              <p className="text-sm text-muted-foreground">
                Permanently delete all your search history
              </p>
            </div>
            <Button variant="outline" className="text-destructive hover:text-destructive">
              Clear History
            </Button>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Reset All Settings</p>
              <p className="text-sm text-muted-foreground">
                Reset all settings to their default values
              </p>
            </div>
            <Button variant="outline" className="text-destructive hover:text-destructive">
              Reset Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
